import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { ShieldCheck, MessageSquare, AlertTriangle, Share2, Calendar, MapPin, Lock, X, Upload, Check } from "lucide-react";
import TrustBadges from "../components/TrustBadges";
import ShareMyExperience from "../components/ShareMyExperience";

const CONCERN_TYPES = [
  { value: "concierge_issue", label: "Concierge issue" },
  { value: "experience_issue", label: "Experience issue" },
  { value: "payment_concern", label: "Payment concern" },
  { value: "safety_concern", label: "Safety concern" },
  { value: "cancellation_issue", label: "Cancellation issue" },
  { value: "other", label: "Other" },
];

const STATUS_STEPS = ["New", "Reviewing", "Waiting Response", "Resolved"];

/**
 * Safety Center — customer-facing Trust & Safety hub (Phase 34).
 *
 * "Contact TREL Support" / phone-style entry points have been removed —
 * TREL is a managed concierge marketplace, not a call center. The single
 * primary safety action is now "Report a concern", which submits a
 * structured form directly to the Admin team. Customers see their own
 * concerns + status (Submitted → Under Review → Resolved) inline so they
 * never wonder whether their concern was heard.
 *
 * Concierges are NOT automatically notified — admin triages first.
 */
export default function SafetyCenter() {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [concerns, setConcerns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shareBooking, setShareBooking] = useState(null);
  const [reportFor, setReportFor] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const loadAll = useCallback(() => {
    api.get("/bookings").then((r) => setBookings(Array.isArray(r.data) ? r.data : []))
      .catch(() => setBookings([]));
    api.get("/concerns").then((r) => setConcerns(Array.isArray(r.data) ? r.data : []))
      .catch(() => setConcerns([]))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  const active = bookings.filter(
    (b) => !["Completed", "Canceled", "Cancelled"].includes(b.status)
  );

  if (!user) {
    return (
      <div className="max-w-2xl mx-auto p-12 text-center">
        <Lock className="mx-auto text-[#064E3B]" size={32} />
        <h1 className="font-serif text-3xl mt-4">Safety Center</h1>
        <p className="text-[#071A2D]/70 mt-2">Please log in to access your bookings and safety tools.</p>
        <Link to="/login" className="btn-coral inline-block mt-6">Log in</Link>
      </div>
    );
  }

  const openReport = (booking) => {
    setReportFor({ booking, concern_type: "concierge_issue", description: "", photo_url: "" });
    setSubmitted(false);
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-10" data-testid="safety-center-page">
      <div className="text-[11px] uppercase tracking-[0.22em] text-[#064E3B] font-semibold">TREL · Trust & Safety</div>
      <h1 className="font-serif text-4xl md:text-5xl mt-2 flex items-center gap-3">
        <ShieldCheck className="text-[#064E3B]" size={32} /> Safety Center
      </h1>
      <p className="text-[#071A2D]/70 mt-3 max-w-2xl">
        Every TREL experience is backed by an Admin team that reviews concerns directly.
        Report any issue and we&apos;ll track it through to resolution — no phone calls,
        no waiting on hold, no guessing.
      </p>

      {/* Trust standards explainer */}
      <div className="grid md:grid-cols-3 gap-4 mt-8">
        <Pillar color="#00AEEF" icon={ShieldCheck} title="Identity Verified"
          body="All TREL Concierges complete identity verification through a secure third-party. We never store ID photos."/>
        <Pillar color="#064E3B" icon={ShieldCheck} title="TREL Certified"
          body="Concierges complete TREL's training and standards agreement before accepting bookings."/>
        <Pillar color="#C9A45C" icon={ShieldCheck} title="TREL Elite"
          body="Optional premium tier with enhanced background screening through our partner Checkr."/>
      </div>

      {/* MY CONCERNS — visible whenever the customer has any */}
      {concerns.length > 0 && (
        <section className="mt-12" data-testid="safety-my-concerns">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif text-2xl">My concerns</h2>
            <span className="text-xs text-[#071A2D]/55">{concerns.length} total</span>
          </div>
          <div className="space-y-3">
            {concerns.map((c) => (
              <ConcernRow key={c.concern_id} concern={c}/>
            ))}
          </div>
        </section>
      )}

      {/* ACTIVE EXPERIENCES — single primary action: Report a concern */}
      <h2 className="font-serif text-2xl mt-12 mb-4">Your active experiences</h2>
      {loading ? (
        <div className="p-8 text-center text-[#071A2D]/60">Loading your bookings…</div>
      ) : active.length === 0 ? (
        <div className="p-8 rounded-2xl card-luxury text-center text-[#071A2D]/60">
          You don&apos;t have any active bookings right now.
          <Link to="/explore" className="underline ml-2">Browse experiences →</Link>
        </div>
      ) : (
        <div className="space-y-4">
          {active.map((b) => (
            <div key={b.booking_id} className="rounded-3xl card-luxury p-5">
              <div className="flex items-start gap-4 flex-wrap">
                {b.experience?.images?.[0] && (
                  <img src={b.experience.images[0]} alt="" className="w-24 h-24 rounded-2xl object-cover"/>
                )}
                <div className="flex-1 min-w-[200px]">
                  <div className="font-medium">{b.experience?.title}</div>
                  <div className="text-xs text-[#071A2D]/60 mt-1 flex items-center gap-2 flex-wrap">
                    <Calendar size={12}/> {b.date} · {b.time} · {b.guests} guests
                    <span className="mx-1">·</span>
                    <MapPin size={12}/> {b.experience?.city}
                  </div>
                  {b.provider_user?.name && (
                    <div className="mt-2 text-sm">
                      with <span className="font-medium">{b.provider_user.name}</span>
                    </div>
                  )}
                  <div className="mt-2">
                    <TrustBadges provider={b.provider_user || {}} size="sm" />
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs px-2 py-1 rounded-full bg-[#064E3B] text-[#FFF8EE] inline-block">{b.status}</div>
                  <div className="font-semibold mt-2">${b.amount}</div>
                </div>
              </div>

              <div className="mt-5 flex flex-wrap gap-2">
                <Link
                  to="/messages"
                  className="rounded-full bg-[#064E3B] text-[#FFF8EE] px-4 py-2 text-xs font-semibold inline-flex items-center gap-1.5 hover:brightness-110"
                  data-testid={`safety-message-concierge-${b.booking_id}`}
                >
                  <MessageSquare size={12}/> Message Concierge
                </Link>
                <button
                  onClick={() => setShareBooking(b)}
                  className="rounded-full bg-[#FF7A45] text-[#FFF8EE] px-4 py-2 text-xs font-semibold inline-flex items-center gap-1.5 hover:brightness-110"
                  data-testid={`safety-share-${b.booking_id}`}
                >
                  <Share2 size={12}/> Share My Experience
                </button>
                <button
                  onClick={() => openReport(b)}
                  className="rounded-full border border-[#FF7A45] text-[#FF7A45] px-4 py-2 text-xs font-semibold inline-flex items-center gap-1.5 hover:bg-[#FF7A45]/10"
                  data-testid={`safety-report-${b.booking_id}`}
                >
                  <AlertTriangle size={12}/> Report a concern
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Standalone Report a concern entry — for issues not tied to a specific booking */}
      <div className="mt-10">
        <button
          onClick={() => openReport(null)}
          data-testid="safety-report-general"
          className="rounded-full bg-[#FF7A45] text-[#FFF8EE] px-6 py-3 text-sm font-semibold inline-flex items-center gap-2 hover:brightness-110"
        >
          <AlertTriangle size={14}/> Report a concern
        </button>
        <span className="text-xs text-[#071A2D]/60 ml-3">
          Submit any issue and the TREL Admin team will review and follow up.
        </span>
      </div>

      <ShareMyExperience
        open={!!shareBooking}
        booking={shareBooking}
        onClose={() => setShareBooking(null)}
      />

      <ReportConcernModal
        isPreview={user.preview_account === true}
        state={reportFor}
        submitted={submitted}
        onChange={(patch) => setReportFor((s) => (s ? { ...s, ...patch } : s))}
        onClose={() => { setReportFor(null); setSubmitted(false); }}
        onSubmitted={() => { setSubmitted(true); loadAll(); }}
      />
    </div>
  );
}

function Pillar({ color, icon: Icon, title, body }) {
  return (
    <div className="rounded-3xl card-luxury p-5" style={{ borderColor: `${color}33` }}>
      <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3"
        style={{ background: `${color}1A`, color }}>
        <Icon size={18}/>
      </div>
      <div className="font-medium">{title}</div>
      <div className="text-xs text-[#071A2D]/65 mt-1 leading-relaxed">{body}</div>
    </div>
  );
}

function ConcernRow({ concern }) {
  const statusIdx = STATUS_STEPS.indexOf(concern.status);
  const labelMap = Object.fromEntries(CONCERN_TYPES.map((t) => [t.value, t.label]));
  const isUrgent = concern.priority === "urgent";
  return (
    <div
      data-testid={`safety-concern-${concern.concern_id}`}
      className="rounded-2xl card-luxury p-5"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#064E3B]">
              {labelMap[concern.concern_type] || concern.concern_type}
            </span>
            {isUrgent && (
              <span className="text-[10px] uppercase tracking-[0.18em] font-semibold px-1.5 py-0.5 rounded-md bg-[#FF7A45]/15 text-[#FF7A45]">
                Urgent
              </span>
            )}
          </div>
          <p className="text-sm text-[#071A2D]/80 mt-2 leading-relaxed line-clamp-2">{concern.description}</p>
          <div className="text-[11px] text-[#071A2D]/55 mt-2">
            Submitted {new Date(concern.created_at).toLocaleDateString()}
          </div>
        </div>
        <span className={`text-xs px-3 py-1 rounded-full font-semibold ${
          concern.status === "Resolved"
            ? "bg-[#064E3B] text-[#FFF8EE]"
            : "bg-[#C9A45C]/20 text-[#5B3F18]"
        }`}>
          {concern.status}
        </span>
      </div>
      {/* Status pipeline */}
      <div className="mt-4 flex items-center gap-2">
        {STATUS_STEPS.map((step, i) => (
          <React.Fragment key={step}>
            <div className={`flex items-center gap-1.5 ${i <= statusIdx ? "text-[#064E3B]" : "text-[#071A2D]/30"}`}>
              <span className={`w-2 h-2 rounded-full ${i <= statusIdx ? "bg-[#064E3B]" : "bg-[#071A2D]/20"}`}/>
              <span className="text-[11px] font-medium">{step}</span>
            </div>
            {i < STATUS_STEPS.length - 1 && (
              <span className={`flex-1 h-px ${i < statusIdx ? "bg-[#064E3B]" : "bg-[#071A2D]/15"}`}/>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

function ReportConcernModal({ isPreview, state, submitted, onChange, onClose, onSubmitted }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  if (!state) return null;

  const submit = async () => {
    setErr(""); setBusy(true);
    // Preview Mode — sample workflow only, no real concern is created.
    if (isPreview) {
      await new Promise((r) => setTimeout(r, 350));
      onSubmitted();
      setBusy(false);
      return;
    }
    try {
      await api.post("/concerns", {
        booking_id: state.booking?.booking_id || null,
        concern_type: state.concern_type,
        description: state.description.trim(),
        photo_url: state.photo_url || "",
      });
      onSubmitted();
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not submit — please try again");
    }
    setBusy(false);
  };

  const handleFile = (file) => {
    if (!file) return;
    // Read file as data URL — backend stores the URL/data ref as-is.
    const reader = new FileReader();
    reader.onload = () => onChange({ photo_url: reader.result?.toString() || "" });
    reader.readAsDataURL(file);
  };

  return (
    <div className="fixed inset-0 bg-[#071A2D]/55 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="report-modal">
      <div className="card-luxury-elevated rounded-3xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-7">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-[#FF7A45] font-semibold">Report</div>
              <h3 className="font-serif text-2xl mt-1.5 flex items-center gap-2">
                <AlertTriangle size={18}/> Report a concern
              </h3>
            </div>
            <button onClick={onClose} aria-label="Close" className="p-1 rounded-full hover:bg-[#071A2D]/5">
              <X size={18}/>
            </button>
          </div>

          {submitted ? (
            <div className="mt-6 text-center" data-testid="report-success">
              <div className="w-12 h-12 mx-auto rounded-full bg-[#064E3B]/10 flex items-center justify-center text-[#064E3B]">
                <Check size={22}/>
              </div>
              <h4 className="font-serif text-lg mt-3">Your concern has been received.</h4>
              <p className="text-sm text-[#071A2D]/65 mt-2 max-w-sm mx-auto leading-relaxed">
                The TREL team will review your experience details and follow up as soon as possible.
              </p>
              <button onClick={onClose} className="btn-coral mt-6">Done</button>
            </div>
          ) : (
            <>
              <p className="text-xs text-[#071A2D]/60 mt-2">
                Tell us what happened. The TREL Admin team reviews every report directly and tracks
                each concern through to resolution.
              </p>

              <div className="mt-5 space-y-4">
                <div>
                  <label className="text-[11px] uppercase tracking-widest text-[#064E3B]/70">Concern type</label>
                  <select
                    data-testid="concern-type"
                    value={state.concern_type}
                    onChange={(e) => onChange({ concern_type: e.target.value })}
                    className="kbd-input"
                  >
                    {CONCERN_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>

                {state.booking && (
                  <div className="rounded-2xl card-luxury-well p-3 text-xs">
                    <div className="text-[10px] uppercase tracking-widest text-[#064E3B]/70">Attached booking</div>
                    <div className="font-medium mt-1">{state.booking.experience?.title}</div>
                    <div className="text-[#071A2D]/60 mt-0.5">
                      {state.booking.date} · {state.booking.time} · {state.booking.guests} guests
                    </div>
                  </div>
                )}

                <div>
                  <label className="text-[11px] uppercase tracking-widest text-[#064E3B]/70">What happened?</label>
                  <textarea
                    data-testid="concern-description"
                    rows={5}
                    placeholder="Describe the situation in your own words…"
                    value={state.description}
                    onChange={(e) => onChange({ description: e.target.value })}
                    className="kbd-input"
                  />
                </div>

                <div>
                  <label className="text-[11px] uppercase tracking-widest text-[#064E3B]/70 flex items-center gap-1.5">
                    <Upload size={11}/> Photo (optional)
                  </label>
                  <input
                    data-testid="concern-photo"
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFile(e.target.files?.[0])}
                    className="text-xs mt-2"
                  />
                  {state.photo_url && (
                    <div className="text-[11px] text-[#064E3B] mt-1">Photo attached ✓</div>
                  )}
                </div>
              </div>

              {err && <div className="text-sm text-[#FF7A45] mt-3" data-testid="report-error">{err}</div>}

              <div className="flex justify-end gap-3 mt-6">
                <button onClick={onClose} className="btn-outline-emerald text-sm">Cancel</button>
                <button
                  onClick={submit}
                  disabled={busy || !state.description.trim()}
                  data-testid="report-submit"
                  className="btn-coral text-sm disabled:opacity-50"
                >
                  {busy ? "Submitting…" : "Submit to TREL Admin"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
